// CallList — the re-canvass Call List (Aug 20, 2026).
//
// Dead leads whose address has taken a NEW >=1" hail hit AFTER the deal went
// cold. Purpose: put a genuinely new date of loss in front of a homeowner who
// already told us no, and turn it into a fresh drone inspection.
//
//   ASM  — filters (city / zip / hail size / storm date) and assigns batches to
//          RMs, one rep at a time or split evenly across several. Unassigned
//          rows carry the amber "Needs an RM" flag.
//   RM   — works their queue: call, text, log the outcome, then either
//          "Schedule app" (revives the existing deal into the drone flow) or
//          "No thanks" (drops off until a NEWER storm hits that address).
//
// Everyone with access sees every row regardless of who owns the underlying
// deal — Greg's call, Aug 20. That only works through the SECURITY DEFINER
// RPCs in lib/recanvass.ts; deals_rm_read would hide most of this list.
//
// ACCESS CONTROL (Aug 31, 2026, Alex's ask): a master switch and an approved-ZIP
// allowlist, both enforced in those same RPCs — this page only renders the
// state. Managers get <AccessPanel/>; the field tier (RM + TL) gets either the
// paused card or a banner naming the ZIPs they're cleared for. Nothing here is
// the control: the server refuses an out-of-scope booking regardless of what
// this component decides to draw.
//
// Route: /call-list. Also embedded as the ASM board's "Call List" tab via the
// exported CallListInner.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import DealLink from "../components/DealLink";
import DealCallText from "../components/DealCallText";
import { formatPhone } from "../lib/phoneFormat";
import AccessPanel from "../components/calllist/AccessPanel";
import { mapsHref } from "../lib/mapsHref";
import {
  fetchRecanvassLeads, fetchRecanvassSummary, fetchRecanvassTrail,
  assignRecanvassLeads, splitRecanvassLeads, logRecanvassOutcome,
  scheduleRecanvassAppointment, metersToMiles, predictBookingMode,
  OUTCOME_LABELS, STATUS_LABELS, DEAD_STAGE_LABELS,
  type RecanvassLead, type RecanvassSummary, type RecanvassStatus,
  type RecanvassOutcome, type RecanvassTrailRow,
} from "../lib/recanvass";

const ASSIGNABLE_ROLES = ["restore_manager", "team_lead", "asm"];
const INPUT = "px-2 py-1.5 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300";
const PAGE = 200;

interface RosterRM { id: string; full_name: string; role: string }

/* ─────────────────────────── small display helpers ───────────────────────── */

function hailTone(size: number): string {
  if (size >= 2) return "bg-rose-100 text-rose-800 border-rose-200";
  if (size >= 1.75) return "bg-orange-100 text-orange-800 border-orange-200";
  if (size >= 1.5) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function statusTone(s: RecanvassStatus): string {
  switch (s) {
    case "new": return "bg-amber-100 text-amber-800 border-amber-200";
    case "assigned": return "bg-sky-100 text-sky-800 border-sky-200";
    case "working": return "bg-indigo-100 text-indigo-800 border-indigo-200";
    case "appt_set": return "bg-emerald-100 text-emerald-800 border-emerald-200";
    default: return "bg-slate-100 text-slate-600 border-slate-200";
  }
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function daysAgo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

/** "Last touch" = the most recent of call / text / logged outcome. */
function lastTouch(l: RecanvassLead): { label: string; at: string } | null {
  const opts: Array<{ label: string; at: string | null }> = [
    { label: "Called", at: l.last_call_at },
    { label: "Texted", at: l.last_text_at },
    { label: l.last_outcome ? OUTCOME_LABELS[l.last_outcome as RecanvassOutcome] ?? l.last_outcome : "Logged", at: l.last_outcome_at },
  ];
  const live = opts.filter((o): o is { label: string; at: string } => !!o.at);
  if (!live.length) return null;
  live.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return live[0];
}

function defaultAppointmentLocal(): string {
  const d = new Date();
  d.setHours(10, 0, 0, 0);
  let added = 0;
  while (added < 2) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ───────────────────────────────── the board ─────────────────────────────── */

export function CallListInner({ embedded = false }: { embedded?: boolean }) {
  const { member, isSuperUser } = useAuth();
  const [params, setParams] = useSearchParams();

  const [summary, setSummary] = useState<RecanvassSummary | null>(null);
  const [roster, setRoster] = useState<RosterRM[]>([]);
  const [leads, setLeads] = useState<RecanvassLead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // filters
  const [mine, setMine] = useState(params.get("mine") === "1");
  const [unassignedOnly, setUnassignedOnly] = useState(params.get("unassigned") === "1");
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [minSize, setMinSize] = useState<string>("");
  const [hailFrom, setHailFrom] = useState("");
  const [hailTo, setHailTo] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [rmFilter, setRmFilter] = useState<string>("");
  const [sort, setSort] = useState<"hail_desc" | "size_desc" | "dead_asc" | "name" | "city">("hail_desc");
  const [offset, setOffset] = useState(0);

  // interaction
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [trail, setTrail] = useState<RecanvassTrailRow[]>([]);
  const [trailLoading, setTrailLoading] = useState(false);
  const [scheduleFor, setScheduleFor] = useState<RecanvassLead | null>(null);
  const [outcomeFor, setOutcomeFor] = useState<RecanvassLead | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  // bulk assign
  const [assignTo, setAssignTo] = useState("");
  const [splitAcross, setSplitAcross] = useState<Set<string>>(new Set());

  const canAssign = !!summary?.can_assign;
  const meId = summary?.me ?? member?.id ?? null;
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  /* ── data ── */
  const loadSummary = useCallback(async () => {
    try { const s = await fetchRecanvassSummary(); if (mountedRef.current) setSummary(s); }
    catch (e) { console.warn("recanvass summary failed", e); }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const statuses: RecanvassStatus[] | null =
        statusFilter === "active" ? null
        : statusFilter === "all" ? ["new", "assigned", "working", "appt_set", "no_thanks", "bad_number", "dnc"]
        : [statusFilter as RecanvassStatus];
      const rows = await fetchRecanvassLeads({
        mine,
        unassigned: unassignedOnly,
        status: statuses,
        assigned: rmFilter ? [rmFilter] : null,
        city: city.trim() || null,
        zip: zip.trim() || null,
        minSize: minSize ? Number(minSize) : null,
        hailFrom: hailFrom || null,
        hailTo: hailTo || null,
        search: search.trim() || null,
        sort,
        limit: PAGE,
        offset,
      });
      if (!mountedRef.current) return;
      setLeads(rows);
      setTotal(rows.length ? Number(rows[0].total_count) : 0);
    } catch (e) {
      if (mountedRef.current) setErr(e instanceof Error ? e.message : "Couldn't load the call list.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [mine, unassignedOnly, statusFilter, rmFilter, city, zip, minSize, hailFrom, hailTo, search, sort, offset]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);

  useEffect(() => {
    supabase.from("team_roster").select("id, full_name, role")
      .eq("is_active", true).eq("on_leave", false).in("role", ASSIGNABLE_ROLES).order("full_name")
      .then(({ data }) => { if (mountedRef.current) setRoster((data ?? []) as RosterRM[]); });
  }, []);

  // debounce the text filters so typing doesn't hammer the RPC
  useEffect(() => {
    const t = setTimeout(() => { void loadLeads(); }, 250);
    return () => clearTimeout(t);
  }, [loadLeads]);

  // live refresh when someone else assigns or works a lead
  useEffect(() => {
    const ch = supabase.channel("recanvass-board")
      .on("postgres_changes", { event: "*", schema: "public", table: "recanvass_leads" }, () => {
        void loadSummary();
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [loadSummary]);

  useEffect(() => { setSelected(new Set()); }, [leads]);

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    if (mine) next.set("mine", "1"); else next.delete("mine");
    if (unassignedOnly) next.set("unassigned", "1"); else next.delete("unassigned");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, unassignedOnly]);

  const say = (msg: string) => { setFlash(msg); setTimeout(() => mountedRef.current && setFlash(null), 4000); };

  const refreshAll = useCallback(async () => { await Promise.all([loadLeads(), loadSummary()]); }, [loadLeads, loadSummary]);

  /* ── actions ── */
  async function openTrail(l: RecanvassLead) {
    if (expanded === l.lead_id) { setExpanded(null); return; }
    setExpanded(l.lead_id);
    setTrailLoading(true);
    try { const t = await fetchRecanvassTrail(l.lead_id); if (mountedRef.current) setTrail(t); }
    catch { if (mountedRef.current) setTrail([]); }
    finally { if (mountedRef.current) setTrailLoading(false); }
  }

  async function doAssign(rmId: string | null) {
    if (!selected.size) return;
    setBusy(true);
    try {
      const r = await assignRecanvassLeads([...selected], rmId);
      // Leads outside the rep's approved ZIPs are skipped server-side rather
      // than failing the batch — say so, or the count silently won't add up.
      const zipNote = r.skipped_zip
        ? ` ${r.skipped_zip} skipped — outside their approved ZIPs.`
        : "";
      say(rmId
        ? `Assigned ${r.assigned} to ${r.rm_name}.${zipNote}`
        : `Returned ${r.assigned} to the unassigned pool.`);
      setSelected(new Set());
      await refreshAll();
    } catch (e) { say(e instanceof Error ? e.message : "Assign failed."); }
    finally { setBusy(false); }
  }

  async function doSplit() {
    if (!selected.size || splitAcross.size < 2) return;
    setBusy(true);
    try {
      const res = await splitRecanvassLeads([...selected], [...splitAcross]);
      say(`Split ${selected.size} leads: ${res.map((r) => `${r.rm_name} ${r.assigned}`).join(" · ")}`);
      setSelected(new Set());
      setSplitAcross(new Set());
      await refreshAll();
    } catch (e) { say(e instanceof Error ? e.message : "Split failed."); }
    finally { setBusy(false); }
  }

  async function doOutcome(l: RecanvassLead, outcome: RecanvassOutcome, note?: string) {
    setBusy(true);
    try {
      await logRecanvassOutcome(l.lead_id, outcome, note);
      say(`${l.homeowner_name ?? "Lead"} — ${OUTCOME_LABELS[outcome]} logged.`);
      setOutcomeFor(null);
      await refreshAll();
    } catch (e) { say(e instanceof Error ? e.message : "Couldn't log that."); }
    finally { setBusy(false); }
  }

  const allChecked = leads.length > 0 && selected.size === leads.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(leads.map((l) => l.lead_id)));
  const toggleOne = (id: string) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const cityOptions = useMemo(
    () => [...new Set(leads.map((l) => l.property_city).filter(Boolean) as string[])].sort(),
    [leads],
  );

  /* ── render ── */
  return (
    <div className={embedded ? "" : "max-w-[1500px] mx-auto px-4 lg:px-8 py-6"}>
      {!embedded && (
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">📞 Call List</h1>
          <p className="text-sm text-slate-600 mt-1">
            Leads whose address took a new <strong>1″+ hail</strong> hit after the deal went cold —
            dead ones, plus live deals sitting <strong>quiet in Waiting 90+ days</strong>.
            A new date of loss is a new reason to call — and a new drone inspection.
            Booking a lead that still has an active RM makes you the <strong>split</strong>, not the owner.
          </p>
        </div>
      )}

      {/* Manager access controls — switch + approved ZIPs. Renders itself null
          for anyone who can't manage access. */}
      {summary?.can_manage_access && <AccessPanel onChanged={() => void refreshAll()} />}

      {/* Field tier, board switched off: this replaces the board entirely. The
          server already returns zero rows, so showing filters would just be a
          dead-end that looks broken. */}
      {summary?.paused_for_me ? (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center">
          <div className="text-3xl mb-2">⏸</div>
          <h2 className="text-lg font-semibold text-slate-900">The Call List is paused</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
            Your ASM has switched the Call List off for now. Nothing is lost — the leads are
            still here and will come back when it's switched on. Check with Alex if you were
            expecting to work it today.
          </p>
        </div>
      ) : (
      <>

      {/* Field tier under a ZIP restriction — name the ZIPs so a rep never
          wonders why the pool looks smaller than the company's. */}
      {summary?.zip_limited && (
        <div className="mb-4 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 text-sm text-sky-900">
          <strong>Approved ZIPs only.</strong> You're seeing leads in{" "}
          {summary.allowed_zips.join(", ")} — the areas your ASM has cleared for calling.
          Ask Alex if you need another ZIP opened up.
        </div>
      )}

      {/* summary chips — the ASM flag lives here */}
      {summary && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => { setUnassignedOnly(true); setMine(false); setStatusFilter("active"); setOffset(0); }}
            className={`px-3 py-2 rounded-lg border text-left transition ${
              summary.unassigned > 0
                ? "bg-amber-50 border-amber-300 hover:bg-amber-100"
                : "bg-slate-50 border-slate-200"
            }`}
          >
            <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">
              ⚠ Needs an RM
            </div>
            <div className="text-xl font-bold text-amber-900">{summary.unassigned}</div>
          </button>
          <Chip label="Assigned" value={summary.assigned} onClick={() => { setStatusFilter("assigned"); setUnassignedOnly(false); setOffset(0); }} />
          <Chip label="Working" value={summary.working} onClick={() => { setStatusFilter("working"); setUnassignedOnly(false); setOffset(0); }} />
          <Chip label="Appts set" value={summary.appt_set} tone="emerald" onClick={() => { setStatusFilter("appt_set"); setUnassignedOnly(false); setOffset(0); }} />
          <Chip label="No thanks" value={summary.no_thanks} onClick={() => { setStatusFilter("no_thanks"); setUnassignedOnly(false); setOffset(0); }} />
          <button
            onClick={() => { setMine(!mine); setUnassignedOnly(false); setOffset(0); }}
            className={`px-3 py-2 rounded-lg border text-left transition ${
              mine ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:bg-slate-50"
            }`}
          >
            <div className={`text-[11px] uppercase tracking-wide font-semibold ${mine ? "text-slate-300" : "text-slate-500"}`}>Mine</div>
            <div className="text-xl font-bold">{summary.mine}</div>
          </button>
        </div>
      )}

      {/* per-rep workload — how the ASM sees the spread */}
      {canAssign && summary && summary.by_rm.length > 0 && (
        <details className="mb-4 rounded-lg border border-slate-200 bg-white">
          <summary className="px-3 py-2 text-sm font-semibold text-slate-700 cursor-pointer">
            Workload by rep ({summary.by_rm.length})
          </summary>
          <div className="px-3 pb-3 flex flex-wrap gap-2">
            {summary.by_rm.map((r) => (
              <button key={r.rm_id}
                onClick={() => { setRmFilter(r.rm_id); setMine(false); setUnassignedOnly(false); setOffset(0); }}
                className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-xs text-left">
                <div className="font-semibold text-slate-800">{r.name}</div>
                <div className="text-slate-500">
                  {r.open} open · <span className="text-emerald-700">{r.appt_set} appt</span> · {r.no_thanks} no
                </div>
              </button>
            ))}
          </div>
        </details>
      )}

      {/* filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3 flex flex-wrap gap-2 items-end">
        <FilterBox label="Search">
          <input value={search} onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            placeholder="Name, street, phone…" className={`${INPUT} w-52`} />
        </FilterBox>
        <FilterBox label="City">
          <input list="rc-cities" value={city} onChange={(e) => { setCity(e.target.value); setOffset(0); }}
            placeholder="Any" className={`${INPUT} w-36`} />
          <datalist id="rc-cities">{cityOptions.map((c) => <option key={c} value={c} />)}</datalist>
        </FilterBox>
        <FilterBox label="Zip">
          <input value={zip} onChange={(e) => { setZip(e.target.value); setOffset(0); }} placeholder="Any" className={`${INPUT} w-24`} />
        </FilterBox>
        <FilterBox label="Min hail">
          <select value={minSize} onChange={(e) => { setMinSize(e.target.value); setOffset(0); }} className={`${INPUT} w-28`}>
            <option value="">1″ (all)</option>
            <option value="1.25">1.25″+</option>
            <option value="1.5">1.5″+</option>
            <option value="1.75">1.75″+</option>
            <option value="2">2″+</option>
          </select>
        </FilterBox>
        <FilterBox label="Storm from">
          <input type="date" value={hailFrom} onChange={(e) => { setHailFrom(e.target.value); setOffset(0); }} className={`${INPUT} w-36`} />
        </FilterBox>
        <FilterBox label="Storm to">
          <input type="date" value={hailTo} onChange={(e) => { setHailTo(e.target.value); setOffset(0); }} className={`${INPUT} w-36`} />
        </FilterBox>
        <FilterBox label="Status">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }} className={`${INPUT} w-40`}>
            <option value="active">Active (default)</option>
            <option value="new">Needs an RM</option>
            <option value="assigned">Assigned</option>
            <option value="working">Working</option>
            <option value="appt_set">Appointment set</option>
            <option value="no_thanks">No thanks</option>
            <option value="all">Everything</option>
          </select>
        </FilterBox>
        <FilterBox label="Assigned to">
          <select value={rmFilter} onChange={(e) => { setRmFilter(e.target.value); setMine(false); setOffset(0); }} className={`${INPUT} w-44`}>
            <option value="">Anyone</option>
            {roster.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        </FilterBox>
        <FilterBox label="Sort">
          <select value={sort} onChange={(e) => { setSort(e.target.value as typeof sort); setOffset(0); }} className={`${INPUT} w-40`}>
            <option value="hail_desc">Newest storm</option>
            <option value="size_desc">Biggest hail</option>
            <option value="dead_asc">Cold the longest</option>
            <option value="name">Homeowner name</option>
            <option value="city">City</option>
          </select>
        </FilterBox>
        <button
          onClick={() => {
            setSearch(""); setCity(""); setZip(""); setMinSize(""); setHailFrom(""); setHailTo("");
            setStatusFilter("active"); setRmFilter(""); setMine(false); setUnassignedOnly(false); setOffset(0);
          }}
          className="px-3 py-2 text-sm rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
        >Clear</button>
      </div>

      {flash && <div className="mb-3 px-3 py-2 rounded-md bg-slate-900 text-white text-sm">{flash}</div>}
      {err && <div className="mb-3 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm">{err}</div>}

      {/* bulk assign bar */}
      {canAssign && selected.size > 0 && (
        <div className="sticky top-16 z-20 mb-3 rounded-lg border border-slate-900 bg-slate-900 text-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{selected.size} selected</span>
            <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
              className="px-2 py-1.5 rounded-md text-slate-900 text-sm">
              <option value="">Assign to…</option>
              {roster.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)}
            </select>
            <button disabled={!assignTo || busy} onClick={() => doAssign(assignTo)}
              className="px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-sm font-semibold">
              Assign
            </button>
            <button disabled={busy} onClick={() => doAssign(null)}
              className="px-3 py-1.5 rounded-md border border-slate-600 hover:bg-slate-800 text-sm">
              Unassign
            </button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 text-sm text-slate-300 hover:text-white">
              Clear
            </button>
          </div>
          <div className="mt-2 pt-2 border-t border-slate-700 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Or split evenly across</span>
            {roster.map((r) => {
              const on = splitAcross.has(r.id);
              return (
                <button key={r.id}
                  onClick={() => setSplitAcross((p) => { const n = new Set(p); if (on) n.delete(r.id); else n.add(r.id); return n; })}
                  className={`px-2 py-1 rounded-md text-xs border ${on ? "bg-orange-500 border-orange-400" : "border-slate-600 hover:bg-slate-800"}`}>
                  {r.full_name}
                </button>
              );
            })}
            <button disabled={splitAcross.size < 2 || busy} onClick={doSplit}
              className="px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-400 disabled:opacity-40 text-sm font-semibold">
              Split {selected.size} across {splitAcross.size || "…"}
            </button>
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500 mb-2">
        {loading ? "Loading…" : `${total.toLocaleString()} lead${total === 1 ? "" : "s"}`}
        {total > leads.length && ` · showing ${offset + 1}–${offset + leads.length}`}
      </div>

      {/* ── desktop table ── */}
      <div className="hidden md:block rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              {canAssign && <th className="p-2 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>}
              <th className="p-2">Homeowner</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Address</th>
              <th className="p-2">City</th>
              <th className="p-2">Zip</th>
              <th className="p-2">New hail</th>
              <th className="p-2">Went cold</th>
              <th className="p-2">Assigned</th>
              <th className="p-2">Last touch</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const touch = lastTouch(l);
              const miles = metersToMiles(l.hail_distance_m);
              const canOpenDeal = isSuperUser || canAssign || l.orig_rm_id === meId;
              // assigned leads belong to their rep — the RPCs enforce this too
              const lockedToOther = !!l.assigned_rm_id && l.assigned_rm_id !== meId && !canAssign;
              const lockMsg = `Assigned to ${l.assigned_rm_name ?? "another rep"} — ask your ASM to reassign it.`;
              return (
                <Fragment key={l.lead_id}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 align-top">
                    {canAssign && (
                      <td className="p-2"><input type="checkbox" checked={selected.has(l.lead_id)} onChange={() => toggleOne(l.lead_id)} /></td>
                    )}
                    <td className="p-2">
                      <div className="font-semibold text-slate-900">
                        {canOpenDeal
                          ? <DealLink id={l.deal_id}>{l.homeowner_name ?? "(no name)"}</DealLink>
                          : (l.homeowner_name ?? "(no name)")}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${statusTone(l.status)}`}>
                          {STATUS_LABELS[l.status]}
                        </span>
                        {l.attempt_count > 0 && <span className="text-[10px] text-slate-500">{l.attempt_count} attempt{l.attempt_count === 1 ? "" : "s"}</span>}
                      </div>
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      {l.homeowner_phone
                        ? <a href={`tel:${l.homeowner_phone}`} className="text-bardin-teal hover:underline">{formatPhone(l.homeowner_phone)}</a>
                        : <span className="text-rose-600 text-xs">no phone</span>}
                      {l.homeowner_phone_alt && (
                        <div className="text-[11px] text-slate-500">alt {formatPhone(l.homeowner_phone_alt)}</div>
                      )}
                      <div className="mt-1">
                        <DealCallText dealId={l.deal_id} homeownerName={l.homeowner_name} homeownerPhone={l.homeowner_phone} compact />
                      </div>
                    </td>
                    <td className="p-2">
                      {l.property_street
                        ? <a href={mapsHref(l)} target="_blank" rel="noreferrer" className="text-bardin-teal hover:underline">{l.property_street}</a>
                        : "—"}
                    </td>
                    <td className="p-2 whitespace-nowrap">{l.property_city ?? "—"}</td>
                    <td className="p-2 whitespace-nowrap">{l.property_zip ?? "—"}</td>
                    <td className="p-2 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 rounded border text-xs font-bold ${hailTone(Number(l.hail_size_in))}`}>
                        {Number(l.hail_size_in).toFixed(2)}″
                      </span>
                      <div className="text-xs text-slate-600 mt-0.5">{fmtDate(l.hail_occurred_at)}</div>
                      {miles != null && <div className="text-[10px] text-slate-400">{miles} mi away</div>}
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      <div className="text-xs text-slate-700">
                        {DEAD_STAGE_LABELS[l.dead_stage ?? ""] ?? l.dead_stage ?? "—"}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {daysAgo(l.dead_at) != null ? `${daysAgo(l.dead_at)}d ago` : ""}
                        {l.orig_rm_name ? ` · ${l.orig_rm_active ? "with" : "was"} ${l.orig_rm_name}` : ""}
                      </div>
                      <BookingChips lead={l} meId={meId} />
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      {l.assigned_rm_name
                        ? <span className="text-xs font-medium text-slate-800">{l.assigned_rm_name}</span>
                        : <span className="px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-semibold">Needs an RM</span>}
                    </td>
                    <td className="p-2 whitespace-nowrap text-xs text-slate-600">
                      {touch ? <>{touch.label}<div className="text-[10px] text-slate-400">{fmtDateTime(touch.at)}</div></> : "—"}
                      {l.last_inbound_at && <div className="text-[10px] text-emerald-700 font-semibold">↩ replied</div>}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setScheduleFor(l)} disabled={lockedToOther}
                          title={lockedToOther ? lockMsg : undefined}
                          className="px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                          📅 Schedule
                        </button>
                        <button onClick={() => setOutcomeFor(l)} disabled={lockedToOther}
                          title={lockedToOther ? lockMsg : undefined}
                          className="px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-100 text-xs disabled:opacity-40 disabled:cursor-not-allowed">
                          Log
                        </button>
                        <button onClick={() => openTrail(l)}
                          className="px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-100 text-xs">
                          {expanded === l.lead_id ? "▲" : "▼"}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === l.lead_id && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={canAssign ? 11 : 10} className="p-3">
                        <Trail rows={trail} loading={trailLoading} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={canAssign ? 11 : 10} className="p-8 text-center text-slate-400 text-sm">
                Nothing matches those filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── mobile cards ── */}
      <div className="md:hidden space-y-2">
        {leads.map((l) => {
          const touch = lastTouch(l);
          const lockedToOther = !!l.assigned_rm_id && l.assigned_rm_id !== meId && !canAssign;
          return (
            <div key={l.lead_id} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{l.homeowner_name ?? "(no name)"}</div>
                  <a href={mapsHref(l)} target="_blank" rel="noreferrer" className="text-xs text-bardin-teal block truncate">
                    {l.property_street} · {l.property_city} {l.property_zip}
                  </a>
                </div>
                <span className={`shrink-0 px-1.5 py-0.5 rounded border text-xs font-bold ${hailTone(Number(l.hail_size_in))}`}>
                  {Number(l.hail_size_in).toFixed(2)}″
                </span>
              </div>
              <div className="mt-1 text-xs text-slate-600">
                New hail {fmtDate(l.hail_occurred_at)} · from {DEAD_STAGE_LABELS[l.dead_stage ?? ""] ?? l.dead_stage}
                {daysAgo(l.dead_at) != null ? ` ${daysAgo(l.dead_at)}d ago` : ""}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${statusTone(l.status)}`}>{STATUS_LABELS[l.status]}</span>
                {l.assigned_rm_name
                  ? <span className="text-[10px] text-slate-500">→ {l.assigned_rm_name}</span>
                  : <span className="px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-semibold">Needs an RM</span>}
                <BookingChips lead={l} meId={meId} mobile />
                {touch && <span className="text-[10px] text-slate-400">· {touch.label} {fmtDate(touch.at)}</span>}
              </div>
              <div className="mt-2 flex items-center gap-2">
                {l.homeowner_phone
                  ? <a href={`tel:${l.homeowner_phone}`} className="text-sm font-medium text-bardin-teal">{formatPhone(l.homeowner_phone)}</a>
                  : <span className="text-xs text-rose-600">no phone</span>}
                <DealCallText dealId={l.deal_id} homeownerName={l.homeowner_name} homeownerPhone={l.homeowner_phone} compact />
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setScheduleFor(l)} disabled={lockedToOther}
                  className="flex-1 px-2 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40">
                  {lockedToOther ? `Assigned to ${l.assigned_rm_name ?? "another rep"}` : "📅 Schedule app"}
                </button>
                <button onClick={() => setOutcomeFor(l)} disabled={lockedToOther}
                  className="px-3 py-2 rounded-md border border-slate-200 text-sm disabled:opacity-40">Log</button>
                <button onClick={() => openTrail(l)}
                  className="px-3 py-2 rounded-md border border-slate-200 text-sm">{expanded === l.lead_id ? "▲" : "▼"}</button>
              </div>
              {expanded === l.lead_id && <div className="mt-2"><Trail rows={trail} loading={trailLoading} /></div>}
            </div>
          );
        })}
        {!loading && leads.length === 0 && (
          <div className="p-8 text-center text-slate-400 text-sm">Nothing matches those filters.</div>
        )}
      </div>

      {/* paging */}
      {total > PAGE && (
        <div className="mt-3 flex items-center justify-center gap-2">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="px-3 py-1.5 rounded-md border border-slate-200 text-sm disabled:opacity-40">← Prev</button>
          <span className="text-xs text-slate-500">{offset + 1}–{offset + leads.length} of {total.toLocaleString()}</span>
          <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}
            className="px-3 py-1.5 rounded-md border border-slate-200 text-sm disabled:opacity-40">Next →</button>
        </div>
      )}

      </>
      )}

      {scheduleFor && (
        <ScheduleModal
          lead={scheduleFor}
          roster={roster}
          defaultOwner={scheduleFor.assigned_rm_id ?? meId}
          meId={meId}
          onClose={() => setScheduleFor(null)}
          onDone={async (msg) => { setScheduleFor(null); say(msg); await refreshAll(); }}
        />
      )}
      {outcomeFor && (
        <OutcomeModal
          lead={outcomeFor}
          busy={busy}
          onClose={() => setOutcomeFor(null)}
          onPick={(o, n) => doOutcome(outcomeFor, o, n)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────── sub-components ──────────────────────────── */

function Chip({ label, value, tone = "slate", onClick }: { label: string; value: number; tone?: string; onClick: () => void }) {
  const cls = tone === "emerald"
    ? "bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-900"
    : "bg-white border-slate-200 hover:bg-slate-50 text-slate-900";
  return (
    <button onClick={onClick} className={`px-3 py-2 rounded-lg border text-left transition ${cls}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </button>
  );
}

function FilterBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Trail({ rows, loading }: { rows: RecanvassTrailRow[]; loading: boolean }) {
  if (loading) return <div className="text-xs text-slate-400">Loading history…</div>;
  if (!rows.length) return <div className="text-xs text-slate-400">No history yet.</div>;
  return (
    <ol className="space-y-1">
      {rows.map((r, i) => (
        <li key={i} className="flex items-start gap-2 text-xs">
          <span className="shrink-0 w-14 text-slate-400">{fmtDate(r.occurred_at)}</span>
          <span className="shrink-0">
            {r.kind === "call" ? "📞" : r.kind === "text" ? "💬" : "•"}
          </span>
          <span className="text-slate-700">
            <strong className="text-slate-900">
              {r.kind === "activity"
                ? (OUTCOME_LABELS[(r.action ?? "") as RecanvassOutcome] ?? r.action)
                : `${r.kind === "call" ? "Call" : "Text"} ${r.action ?? ""}`}
            </strong>
            {r.detail ? ` — ${r.detail}` : ""}
            {r.actor_name ? <span className="text-slate-400"> · {r.actor_name}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}

function OutcomeModal({
  lead, busy, onClose, onPick,
}: {
  lead: RecanvassLead; busy: boolean;
  onClose: () => void; onPick: (o: RecanvassOutcome, note?: string) => void;
}) {
  const [note, setNote] = useState("");
  const quick: RecanvassOutcome[] = ["no_answer", "left_vm", "callback", "interested", "note"];
  const closers: RecanvassOutcome[] = ["no_thanks", "bad_number", "already_roofed", "dnc"];
  return (
    <Modal title={`Log a call — ${lead.homeowner_name ?? "lead"}`} onClose={onClose}>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Optional note (what they said, when to call back…)"
        className="w-full border border-slate-200 rounded-md p-2 text-sm mb-3" />
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Keep working</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {quick.map((o) => (
          <button key={o} disabled={busy} onClick={() => onPick(o, note)}
            className="px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-100 text-sm disabled:opacity-40">
            {OUTCOME_LABELS[o]}
          </button>
        ))}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Take it off the list</div>
      <div className="flex flex-wrap gap-2">
        {closers.map((o) => (
          <button key={o} disabled={busy} onClick={() => onPick(o, note)}
            className="px-3 py-1.5 rounded-md border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800 text-sm disabled:opacity-40">
            {OUTCOME_LABELS[o]}
          </button>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        “No thanks” and “Bad number” come back automatically if a <strong>newer</strong> 1″+ storm hits this address.
        “Already re-roofed” and “Do not contact” never return.
      </p>
    </Modal>
  );
}

/** What booking this lead would do, before anyone taps it. The server decides;
 *  this mirrors predictBookingMode for the person viewing the row. */
function BookingChips({ lead: l, meId, mobile }: { lead: RecanvassLead; meId: string | null; mobile?: boolean }) {
  const chip = "px-1.5 py-0.5 rounded border text-[10px] font-semibold";
  const block = mobile ? "" : "inline-block mt-1 mr-1";
  if (l.canvasser_lead) {
    const takesOver = l.orig_rm_active && !!l.orig_rm_id && l.orig_rm_id !== meId;
    return (
      <>
        <span
          title={`${l.source_canvasser_name ?? "A canvasser"} brought this lead in. Whoever books it owns it alone, and the 5-point canvasser carve-out applies.`}
          className={`${block} ${chip} border-amber-200 bg-amber-50 text-amber-800`}>
          Canvasser lead · 5 pts off
        </span>
        {takesOver && (
          <span
            title={`${l.orig_rm_name} owns it now. Booking it moves the deal to the RM who books${l.orig_split_name ? ` and takes ${l.orig_split_name} off the split` : ""}.`}
            className={`${block} ${chip} border-rose-200 bg-rose-50 text-rose-800`}>
            Takes it over from {l.orig_rm_name ?? "the owner"}
          </span>
        )}
      </>
    );
  }
  if (l.orig_rm_active && l.orig_rm_id !== meId) {
    return (
      <span
        title={`${l.orig_rm_name} still owns this deal. Booking it makes you the split RM, not the owner.`}
        className={`${block} ${chip} border-violet-200 bg-violet-50 text-violet-800`}>
        {mobile ? `${l.orig_rm_name ?? "Owned"} keeps it · you'd be split` : "You'd be split"}
      </span>
    );
  }
  return null;
}

function ScheduleModal({
  lead, roster, defaultOwner, meId, onClose, onDone,
}: {
  lead: RecanvassLead; roster: RosterRM[]; defaultOwner: string | null; meId: string | null;
  onClose: () => void; onDone: (msg: string) => void;
}) {
  const [when, setWhen] = useState(defaultAppointmentLocal());
  const [owner, setOwner] = useState(defaultOwner ?? "");
  const [phone, setPhone] = useState(lead.homeowner_phone ?? "");
  const [email, setEmail] = useState(lead.homeowner_email ?? "");
  const [insurance, setInsurance] = useState(lead.insurance_company ?? "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Mirror of the server rule so the modal states the outcome before the click.
  // recanvass_schedule_appointment still decides; this only has to agree. The
  // server credits the picker, else the assigned rep, else whoever clicks.
  const creditId = owner || lead.assigned_rm_id || meId || null;
  const mode = predictBookingMode(lead, creditId);
  const creditName = creditId ? roster.find((r) => r.id === creditId)?.full_name ?? null : null;
  // Canvasser lead: who comes off the deal when it moves to the RM who books.
  const ownerComesOff = mode === "canvasser" && lead.orig_rm_active
    && !!lead.orig_rm_id && lead.orig_rm_id !== creditId;
  const splitComesOff = mode === "canvasser" && !!lead.orig_split_id && lead.orig_split_id !== creditId;
  const creditedCanvasser = lead.source_canvasser_name && lead.source_canvasser_name !== "Canvass Team"
    ? lead.source_canvasser_name : null;

  async function submit() {
    if (!when) { setErr("Pick a date and time."); return; }
    setBusy(true); setErr(null);
    try {
      const res = await scheduleRecanvassAppointment({
        leadId: lead.lead_id,
        appointmentAt: new Date(when).toISOString(),
        ownerId: owner || null,
        phone, email, insurance, notes,
      });
      const who = res.credited_name ?? "you";
      const hn = lead.homeowner_name ?? "the homeowner";
      if (res.mode === "split") {
        onDone(`Appointment set for ${hn} — ${lead.orig_rm_name ?? "the original RM"} keeps the deal, ${who} added as split.`
          + (res.displaced_split_name ? ` ${res.displaced_split_name} was replaced as split and notified.` : ""));
      } else if (res.mode === "canvasser") {
        onDone(`Appointment set for ${hn} — canvasser lead, ${who} owns it with the 5-point carve-out`
          + (res.canvasser_name && res.canvasser_name !== "Canvass Team" ? ` credited to ${res.canvasser_name}.` : ".")
          + (res.replaced_owner_name ? ` ${res.replaced_owner_name} came off the deal and was notified.` : "")
          + (res.displaced_split_name ? ` ${res.displaced_split_name} came off the split and was notified.` : ""));
      } else {
        onDone(`Appointment set for ${hn} — deal is back in the drone flow, owned by ${who}.`);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't set the appointment.");
      setBusy(false);
    }
  }

  return (
    <Modal title="Schedule the appointment" onClose={onClose}>
      <div className="mb-3 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
        This <strong>revives the existing deal</strong> at {lead.property_street} — no duplicate.
        It moves to <strong>Appointment Received</strong>, the new date of loss is stamped as{" "}
        <strong>{fmtDate(lead.hail_occurred_at)}</strong> ({Number(lead.hail_size_in).toFixed(2)}″ hail),
        the knock pin comes off do-not-knock, and a fresh storm report generates automatically.
      </div>
      {mode === "canvasser" ? (
        <div className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
          <strong>Canvasser lead</strong>
          {lead.source_canvasser_name ? <> — {lead.source_canvasser_name} brought this one in</> : null}.{" "}
          {creditName ? <>{creditName} will own it <strong>alone</strong></> : <>You'll own it <strong>alone</strong></>}
          {ownerComesOff ? <>: <strong>{lead.orig_rm_name}</strong> comes off the deal</> : null}
          {splitComesOff ? <>{ownerComesOff ? " and " : ": "}<strong>{lead.orig_split_name}</strong> comes off the split</> : null}
          {ownerComesOff || splitComesOff ? <>. They'll get a notification and keep the points they already earned on it</> : null}.
          {" "}The 5-point canvasser carve-out applies{creditedCanvasser ? <>, credited to {creditedCanvasser}</> : null}.
        </div>
      ) : mode === "split" ? (
        <div className="mb-3 p-2 rounded-md bg-violet-50 border border-violet-200 text-xs text-violet-900">
          <strong>{lead.orig_rm_name ?? "The current RM"} keeps this deal.</strong>{" "}
          {creditName ? <>{creditName} goes on as the <strong>split RM</strong></> : <>You go on as the <strong>split RM</strong></>} —
          half each. They'll get a notification.
          {" "}No canvasser carve-out: it's an RM's own lead.
        </div>
      ) : (
        <div className="mb-3 p-2 rounded-md bg-sky-50 border border-sky-200 text-xs text-sky-900">
          No active RM owns this one, so {creditName ? <>{creditName} owns it</> : <>you own it</>}.
          {" "}No canvasser carve-out: it's an RM's own lead.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Appointment">
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={`${INPUT} w-full`} />
        </Field>
        <Field label={mode === "split" ? "Credit the split to" : "Deal owner (RM)"}>
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className={`${INPUT} w-full`}>
            <option value="">{lead.assigned_rm_name ? `${lead.assigned_rm_name} (assigned)` : "Me"}</option>
            {roster.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        </Field>
        <Field label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} className={`${INPUT} w-full`} /></Field>
        <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} className={`${INPUT} w-full`} /></Field>
        <Field label="Insurance carrier">
          <input value={insurance} onChange={(e) => setInsurance(e.target.value)} className={`${INPUT} w-full`} placeholder="Confirm at the door" />
        </Field>
      </div>
      <Field label="Notes for the inspection">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${INPUT} w-full`} />
      </Field>
      {err && <div className="mt-2 text-sm text-rose-700">{err}</div>}
      <div className="mt-4 flex gap-2 justify-end">
        <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-md border border-slate-200 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy}
          className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-40">
          {busy ? "Setting…" : "Set appointment"}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 mt-2">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[120] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog" aria-modal="true">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CallList() {
  return <CallListInner />;
}
